import type { GradientStyle } from '../types';
import {
  DEFAULT_GRADIENT_CSS,
  buildButtonBackground,
  buildGradient as buildAdventureGradient,
  getDefaultButtonColor,
  getDefaultGradient,
  isValidColor,
} from '@storynity/ui';

// Theme configuration for consistent styling across the application.
// Adventure-style CSS helpers come from @storynity/ui; pageStyles remain app-local.
export const theme = {
  // Background gradients
  gradients: {
    primary: DEFAULT_GRADIENT_CSS, // Storynity fallback (Shadow Realm)
    secondary: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 50%, #db2777 100%)',
    success: 'linear-gradient(135deg, #059669 0%, #0d9488 50%, #0891b2 100%)',
    warning: 'linear-gradient(135deg, #d97706 0%, #dc2626 50%, #b91c1c 100%)',
  },

  // Color palette
  colors: {
    primary: {
      bg: '#64748b',
      hover: '#475569',
      text: '#ffffff',
      muted: '#cbd5e1',
    },
    secondary: {
      bg: '#6b7280',
      hover: '#4b5563',
      text: '#ffffff',
      muted: '#e5e7eb',
    },
    accent: {
      bg: '#8b5cf6',
      hover: '#7c3aed',
      text: '#ffffff',
      muted: '#ede9fe',
    },
    surface: {
      glass: 'rgba(255, 255, 255, 0.1)',
      glassStrong: 'rgba(255, 255, 255, 0.9)',
      glassWeak: 'rgba(255, 255, 255, 0.05)',
      glassDark: 'rgba(0, 0, 0, 0.3)',
    },
    text: {
      primary: '#ffffff',
      secondary: '#e0e7ff',
      muted: '#c7d2fe',
      error: '#fca5a5',
    },
  },

  spacing: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem',
  },

  borderRadius: {
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
  },

  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },

  animations: {
    fast: '150ms',
    normal: '300ms',
    slow: '500ms',
  },
} as const;

// Page layout styles (Storynity app chrome — not part of @storynity/ui)
export const pageStyles = {
  gradientBackground: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    background: theme.gradients.primary,
  },

  container: {
    maxWidth: '28rem',
    margin: '0 auto',
    textAlign: 'center' as const,
    padding: '0.5rem 1.5rem 1.5rem 1.5rem',
  },

  header: {
    marginBottom: '1rem',
  },

  logo: {
    width: '6rem',
    height: '6rem',
    margin: '0 auto 1rem',
    backgroundColor: theme.colors.primary.bg,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  logoIcon: {
    width: '3rem',
    height: '3rem',
    color: theme.colors.primary.text,
  },

  title: {
    fontSize: '1.875rem',
    fontWeight: 'bold',
    color: theme.colors.text.primary,
    marginBottom: '0.5rem',
  },

  subtitle: {
    color: theme.colors.text.secondary,
  },

  formContainer: {
    backgroundColor: theme.colors.surface.glassDark,
    backdropFilter: 'blur(8px)',
    padding: '1rem 1rem 2rem 1rem',
    borderRadius: theme.borderRadius.lg,
    boxShadow: theme.shadows.xl,
    border: `1px solid rgba(255, 255, 255, 0.15)`,
  },

  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1.5rem',
  },

  input: {
    backgroundColor: theme.colors.surface.glassStrong,
  },

  button: {
    primary: {
      backgroundColor: theme.colors.primary.bg,
      color: theme.colors.primary.text,
      '&:hover': {
        backgroundColor: theme.colors.primary.hover,
      },
    },
    secondary: {
      backgroundColor: theme.colors.secondary.bg,
      color: theme.colors.secondary.text,
      '&:hover': {
        backgroundColor: theme.colors.secondary.hover,
      },
    },
    ghost: {
      backgroundColor: 'transparent',
      color: theme.colors.text.secondary,
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        color: theme.colors.text.primary,
      },
    },
  },

  error: {
    backgroundColor: 'rgba(254, 226, 226, 0.9)',
    border: '1px solid #fecaca',
    borderRadius: theme.borderRadius.md,
    padding: '1rem',
  },

  link: {
    primary: {
      color: theme.colors.text.secondary,
      '&:hover': {
        color: theme.colors.text.primary,
      },
    },
    secondary: {
      color: theme.colors.text.muted,
      '&:hover': {
        color: theme.colors.text.secondary,
      },
    },
  },

  success: {
    container: {
      textAlign: 'center' as const,
    },
    icon: {
      width: '3rem',
      height: '3rem',
      margin: '0 auto 1rem',
      backgroundColor: 'rgba(34, 197, 94, 0.1)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    iconSvg: {
      width: '1.5rem',
      height: '1.5rem',
      color: '#22c55e',
    },
  },
} as const;

/**
 * Build a CSS linear gradient from engine `GradientStyle` (`colors` / `angle`).
 * Adapts to `@storynity/ui` adventure-style field names.
 */
export function buildGradient(style: GradientStyle): string {
  const input: {
    background_gradient: string[];
    gradient_angle?: number;
  } = {
    background_gradient: style.colors ?? [],
  };
  if (style.angle !== undefined) {
    input.gradient_angle = style.angle;
  }
  return buildAdventureGradient(input);
}

export {
  getDefaultGradient,
  getDefaultButtonColor,
  buildButtonBackground,
  isValidColor,
};

export default theme;
